import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getBlogPosts, getBlogCategories } from "@/lib/api";
import { motion } from "framer-motion";
import { BookOpen, Calendar, Eye, ArrowRight, Search as SearchIcon, X, MapPin, Store } from "lucide-react";

// Public blog index. When the visitor picks the Dealer Recruitment category
// we surface three extra filters (State / City / Blog Type) and a "not
// seeing your city?" fallback CTA that points to /become-dealer so a
// prospective operator never dead-ends on this page.
const RECRUITMENT_SLUG = "dealer-recruitment";

const VARIANT_LABELS = {
  own_tw_store: "Open a Total Wireless Store",
  cell_phone_store: "Open a Cell Phone Store",
  repair_store: "Open a Cell Phone Repair Store",
  buy_phone_store: "Buy a Cell Phone Store",
};

// Parse "Looking to Open ... in {City}, {STATE}" → { city, state }
function parseCityState(title) {
  const m = (title || "").match(/\bin\s+([^,]+),\s+([A-Za-z ]{2,})\s*$/);
  if (!m) return { city: "", state: "" };
  return { city: m[1].trim(), state: m[2].trim() };
}

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQ, setSearchQ] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postsData, categoriesData] = await Promise.all([
          getBlogPosts(null, selectedCategory),
          getBlogCategories(),
        ]);
        setPosts(postsData);
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching blog data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // Reset dealer-recruitment sub-filters when leaving the category
    if (selectedCategory !== RECRUITMENT_SLUG) {
      setStateFilter("all");
      setCityFilter("all");
      setTypeFilter("all");
    }
  }, [selectedCategory]);

  const isRecruitment = selectedCategory === RECRUITMENT_SLUG;

  // Extract state + city option lists from the loaded posts (dealer-
  // recruitment posts encode "in {city}, {state}" in the title so we parse
  // once and reuse for both dropdowns and filtering).
  const { states, cities, filtered } = useMemo(() => {
    const stateSet = new Set();
    const cityMap = new Map(); // city → state (so we can scope by selected state)
    const enriched = posts.map((p) => {
      const { city, state } = parseCityState(p.title);
      if (state) stateSet.add(state);
      if (city && state) cityMap.set(city, state);
      return { ...p, _city: city, _state: state };
    });
    const q = searchQ.trim().toLowerCase();
    const list = enriched.filter((p) => {
      if (q) {
        const hay = `${p.title} ${p.excerpt || ""} ${p.category || ""} ${p._city} ${p._state}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (isRecruitment) {
        if (stateFilter !== "all" && p._state !== stateFilter) return false;
        if (cityFilter !== "all" && p._city !== cityFilter) return false;
        if (typeFilter !== "all" && p.variant_angle !== typeFilter) return false;
      }
      return true;
    });
    return {
      states: Array.from(stateSet).sort(),
      cities: Array.from(cityMap.entries())
        .filter(([, st]) => stateFilter === "all" || st === stateFilter)
        .map(([c]) => c)
        .sort(),
      filtered: list,
    };
  }, [posts, searchQ, isRecruitment, stateFilter, cityFilter, typeFilter]);

  const clearAllFilters = () => {
    setSearchQ("");
    setStateFilter("all");
    setCityFilter("all");
    setTypeFilter("all");
  };
  const anyFilterActive =
    !!searchQ || stateFilter !== "all" || cityFilter !== "all" || typeFilter !== "all";

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="bg-navy py-20" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="label-style text-teal mb-4">Our Blog</p>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl text-white mb-6">
              News, Tips & Updates
            </h1>
            <p className="text-slate-300 text-lg font-body">
              Stay up to date with the latest news from Prime Media Wireless and the wireless industry.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter + Search */}
      {categories.length > 0 && (
        <section className="bg-white border-b border-slate-200 py-4" data-testid="blog-categories">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedCategory === null ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(null)}
                className={selectedCategory === null ? "bg-teal hover:bg-teal-dark text-white" : ""}
                data-testid="blog-category-all"
              >
                All Posts
              </Button>
              {categories.map((cat) => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.slug ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.slug)}
                  className={selectedCategory === cat.slug ? "bg-teal hover:bg-teal-dark text-white" : ""}
                  data-testid={`blog-category-${cat.slug}`}
                >
                  {cat.name}
                </Button>
              ))}
            </div>

            {/* Search field — always below the category chips */}
            <div className="relative max-w-xl">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search articles by title, city, or keyword…"
                className="pl-9 pr-9"
                data-testid="blog-search-input"
              />
              {searchQ && (
                <button
                  type="button"
                  onClick={() => setSearchQ("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-navy"
                  data-testid="blog-search-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Dealer-recruitment sub-filters */}
            {isRecruitment && (
              <div className="flex flex-wrap items-center gap-2" data-testid="recruitment-filters">
                <Select
                  value={stateFilter}
                  onValueChange={(v) => { setStateFilter(v); setCityFilter("all"); }}
                >
                  <SelectTrigger className="w-40" data-testid="recruitment-filter-state">
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All states</SelectItem>
                    {states.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={cityFilter} onValueChange={setCityFilter}>
                  <SelectTrigger className="w-48" data-testid="recruitment-filter-city">
                    <SelectValue placeholder="City" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All cities</SelectItem>
                    {cities.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-56" data-testid="recruitment-filter-type">
                    <SelectValue placeholder="Blog type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All blog types</SelectItem>
                    {Object.entries(VARIANT_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {anyFilterActive && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="text-slate-500 hover:text-navy"
                    data-testid="recruitment-filter-clear"
                  >
                    <X className="w-3.5 h-3.5 mr-1" /> Clear
                  </Button>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Blog Posts Grid */}
      <section className="py-16 bg-slate-50" data-testid="blog-posts-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal mx-auto"></div>
              <p className="text-slate-500 mt-4 font-body">Loading posts...</p>
            </div>
          ) : (
            <>
              {/* Result counter */}
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-slate-500 font-body" data-testid="blog-result-count">
                  Showing {filtered.length} of {posts.length} posts
                  {anyFilterActive && " (filtered)"}
                </p>
              </div>

              {filtered.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 font-body text-lg">
                    {anyFilterActive ? "No posts match your filters." : "No blog posts yet"}
                  </p>
                  <p className="text-slate-400 text-sm font-body mt-1">
                    {anyFilterActive ? "Try a different city, state, or blog type." : "Check back soon for new content!"}
                  </p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filtered.map((post, index) => (
                    <motion.div
                      key={post.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index, 8) * 0.05 }}
                    >
                      <Link to={`/blog/${post.slug}`} data-testid={`blog-post-card-${post.slug}`}>
                        <Card className="border-slate-200 hover-lift h-full cursor-pointer group overflow-hidden">
                          {post.featured_image && (
                            <div className="aspect-video overflow-hidden bg-slate-100">
                              <img
                                src={post.featured_image}
                                alt={post.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                          )}
                          <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-3 text-xs text-slate-500 font-body">
                              {post.category && (
                                <span className="px-2 py-0.5 bg-teal/10 text-teal rounded-full font-medium">
                                  {post.category.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(post.publish_date || post.created_at).toLocaleDateString()}
                              </span>
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3" />
                                {post.views || 0}
                              </span>
                            </div>
                            <h3 className="font-heading font-semibold text-navy text-lg mb-2 group-hover:text-teal transition-colors">
                              {post.title}
                            </h3>
                            <p className="text-slate-600 text-sm font-body line-clamp-3 mb-4">
                              {post.excerpt}
                            </p>
                            <div className="flex items-center text-teal text-sm font-medium">
                              Read More
                              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Generic dealer-recruitment fallback: shown whenever the visitor
                  is on the recruitment category so ANYONE who doesn't see
                  their exact city or state still has a clear path to
                  /become-dealer. Sits below the grid so it doesn't compete
                  with any matching city posts. */}
              {isRecruitment && (
                <div className="mt-12">
                  <Card
                    className="border-teal/30 bg-gradient-to-br from-navy to-navy-dark text-white overflow-hidden"
                    data-testid="recruitment-fallback-cta"
                  >
                    <CardContent className="p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6">
                      <div className="hidden md:flex w-16 h-16 rounded-xl bg-teal/20 items-center justify-center flex-shrink-0">
                        <Store className="w-8 h-8 text-teal" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="label-style text-teal mb-2">Don't see your city or state?</p>
                        <h3 className="font-heading font-bold text-2xl md:text-3xl mb-3">
                          Open Your Own Total Wireless Store — Anywhere in the U.S.
                        </h3>
                        <p className="text-slate-300 font-body max-w-3xl">
                          Prime Media Wireless is a Total Wireless Master Agent — one of the fastest-growing
                          in the country — helping ambitious operators launch their own Total Wireless stores.
                          This isn't a job. It's real ownership, real equity, and a real future backed by
                          Verizon-powered service and US-based support. If your city isn't listed above, we're
                          still actively recruiting — reach out and we'll walk you through everything.
                        </p>
                        <div className="mt-5 flex flex-wrap gap-3">
                          <Link to="/become-dealer">
                            <Button
                              className="bg-teal hover:bg-teal-dark text-white"
                              data-testid="recruitment-fallback-become-dealer-btn"
                            >
                              Become a Dealer
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                          </Link>
                          <Link to="/totalwirelessmasteragent">
                            <Button
                              variant="outline"
                              className="border-white/30 text-white hover:bg-white/10"
                              data-testid="recruitment-fallback-learn-more-btn"
                            >
                              <MapPin className="w-4 h-4 mr-2" />
                              Learn About Prime Media Wireless
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
